import { useTranslation } from 'react-i18next'

const FALLBACK_STRINGS: Record<string, string> = {
  placeholder: 'Seleccionar...',
  searchPlaceholder: 'Buscar...',
  noResults: 'No se encontraron resultados',
  loading: 'Cargando...',
  minChars: 'Escribe al menos {{count}} caracteres',
  error: 'Ocurrió un error: {{message}}',
  'error.network': 'No se pudo conectar con el servidor. Revisa tu conexión a internet.',
  'error.unauthorized': 'Tu sesión expiró. Inicia sesión de nuevo.',
  'error.forbidden': 'No tienes permisos para ver esta información.',
  'error.notFound': 'No se encontró la información solicitada.',
  'error.timeout': 'La solicitud tardó demasiado. Intenta de nuevo.',
  'error.rateLimited': 'Demasiadas solicitudes. Espera un momento e intenta de nuevo.',
  'error.server': 'Ocurrió un error en el servidor. Intenta más tarde.',
  notFound: '[Registro no encontrado]',
  clear: 'Limpiar selección',
  createNew: 'Crear nuevo',
  searchOperatorStartsWith: 'Empieza con',
  searchOperatorContains: 'Contiene',
  exactMatchNotFound: 'No se encontró ningún registro con el código exacto "{{value}}"',
  open: 'Abrir lista',
  close: 'Cerrar lista',
  'announcements.loading': 'Cargando resultados',
  'announcements.noResults': 'No se encontraron resultados',
  'announcements.cleared': 'Selección eliminada',
  'announcements.opened': 'Lista abierta, {{count}} resultados disponibles',
  'announcements.closed': 'Lista cerrada',
  'announcements.selected': '{{value}} seleccionado',
  'announcements.resultsLoaded': '{{count}} resultados cargados',
  'announcements.exactMatchNotFound': 'No se encontró ningún registro con el código exacto {{value}}',
  'announcements.exactMatchResolving': 'Buscando código exacto {{value}}',
  'validation.required': 'Este campo es requerido',
  'validation.requiredIndicator': '*',
}

function interpolate(template: string, params?: Record<string, unknown>): string {
  if (!params) return template
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(params[key] ?? ''))
}

export type TFunction = (key: string, params?: Record<string, unknown>) => string

interface UseTranslationSafeResult {
  t: TFunction
  i18n: ReturnType<typeof useTranslation>['i18n'] | null
}

export function useTranslationSafe(): UseTranslationSafeResult {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { t: i18nT, i18n } = useTranslation('lookupField')
    // If i18n is not initialized, fall back
    if (!i18n.isInitialized) {
      throw new Error('i18n not initialized')
    }
    return {
      t: (key: string, params?: Record<string, unknown>) =>
        params ? i18nT(key, params as Record<string, string | number>) : i18nT(key),
      i18n,
    }
  } catch {
    // Return fallback t function when i18next is not available
    const fallbackT: TFunction = (key: string, params?: Record<string, unknown>) => {
      const template = FALLBACK_STRINGS[key] ?? key
      return interpolate(template, params)
    }
    return { t: fallbackT, i18n: null }
  }
}
